import { useState } from 'react';
import type { ChatQuestion, QuestionAnswer } from '@localagent/sdk';
import { cn } from '@localagent/ui/lib/utils';

export interface QuestionCardProps {
  question: ChatQuestion;
  onSubmit: (answer: QuestionAnswer) => Promise<unknown>;
}

const ANSWER_TEXT_MAX_LENGTH = 8000;

function askerDisplay(question: ChatQuestion): string {
  return question.askerIsPrimary ? question.askerName : `${question.askerName} (subagent)`;
}

function recordAnswer(question: ChatQuestion): string {
  if (question.status === 'declined') {
    return `Used their judgment: ${question.default}`;
  }
  if (question.status === 'cancelled') {
    return question.cancelReason === null ? 'Cancelled' : `Cancelled: ${question.cancelReason}`;
  }
  const parts = question.answerOptionIndexes.map((index) => question.options[index].label);
  if (question.answerText !== null && question.answerText.trim() !== '') {
    parts.push(question.answerText);
  }
  return parts.join('; ');
}

function QuestionRecord({ question, asker }: { question: ChatQuestion; asker: string }) {
  return (
    <div
      data-testid="question-record"
      data-question-id={question.questionId}
      className="rounded-lg border border-border-subtle bg-card px-3 py-2"
    >
      <p data-testid="question-card-asker" className="text-micro text-muted-foreground">
        {asker}
      </p>
      <p data-testid="question-card-text" className="text-caption text-foreground">
        {question.question}
      </p>
      <p
        data-testid="question-record-answer"
        className={cn('mt-1 text-caption', question.status === 'cancelled' ? 'text-muted-foreground' : 'text-success-text')}
      >
        {recordAnswer(question)}
      </p>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

export function QuestionCard({ question, onSubmit }: QuestionCardProps) {
  const [selected, setSelected] = useState<number[]>([]);
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const asker = askerDisplay(question);

  if (question.status !== 'open') {
    return <QuestionRecord question={question} asker={asker} />;
  }

  const trimmed = text.trim();
  const canAnswer = !pending && (selected.length > 0 || trimmed !== '');
  const inputName = `question-${question.questionId}`;

  const toggle = (index: number) => {
    if (question.multiSelect) {
      setSelected((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
    } else {
      setSelected((prev) => (prev.length === 1 && prev[0] === index ? [] : [index]));
    }
  };

  const submit = async (answer: QuestionAnswer) => {
    setPending(true);
    setError(null);
    try {
      await onSubmit(answer);
    } catch (err) {
      setError(errorMessage(err));
      setPending(false);
    }
  };

  const submitAnswer = () => {
    const answer: { optionIndexes?: number[]; text?: string } = {};
    if (selected.length > 0) answer.optionIndexes = [...selected].sort((a, b) => a - b);
    if (trimmed !== '') answer.text = trimmed;
    void submit(answer);
  };

  return (
    <div
      data-testid="question-card"
      data-question-id={question.questionId}
      className="rounded-lg border border-border bg-card p-3"
    >
      <p data-testid="question-card-asker" className="text-micro text-muted-foreground">
        {asker}
      </p>
      <p data-testid="question-card-text" className="mt-0.5 text-body text-foreground">
        {question.question}
      </p>
      {question.addresseeKind === 'participant' ? (
        <p data-testid="question-card-agent-addressed" className="mt-1 text-micro text-muted-foreground">
          Asked of another agent; your answer goes straight to the asker
        </p>
      ) : null}
      {question.options.length > 0 ? (
        <div
          role={question.multiSelect ? 'group' : 'radiogroup'}
          aria-label="Options"
          className="mt-2 flex flex-col gap-1"
        >
          {question.options.map((option, index) => {
            const checked = selected.includes(index);
            return (
              <label
                key={`${index}-${option.label}`}
                data-testid={`question-card-option-${index}`}
                className={cn(
                  'flex cursor-pointer items-start gap-2 rounded px-2 py-1.5',
                  checked ? 'bg-selected' : 'hover:bg-accent',
                  pending && 'cursor-not-allowed opacity-50',
                )}
              >
                <input
                  type={question.multiSelect ? 'checkbox' : 'radio'}
                  name={inputName}
                  checked={checked}
                  disabled={pending}
                  onChange={() => toggle(index)}
                  onClick={() => {
                    if (!question.multiSelect && checked) toggle(index);
                  }}
                  className="mt-0.5"
                />
                <span className="flex flex-col">
                  <span className="text-label text-foreground">{option.label}</span>
                  {option.description ? (
                    <span className="text-caption text-muted-foreground">{option.description}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      ) : null}
      <textarea
        data-testid="question-card-text-input"
        value={text}
        disabled={pending}
        maxLength={ANSWER_TEXT_MAX_LENGTH}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your own answer"
        rows={2}
        className="mt-2 w-full resize-y rounded border border-input bg-background px-2 py-1.5 text-body text-foreground placeholder:text-muted-foreground disabled:opacity-50"
      />
      {error !== null ? (
        <p data-testid="question-card-error" role="alert" className="mt-1 text-caption text-error-text">
          {error}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="question-card-submit"
          disabled={!canAnswer}
          onClick={submitAnswer}
          className="rounded bg-primary px-3 py-1 text-label text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Answer
        </button>
        <button
          type="button"
          data-testid="question-card-decline"
          disabled={pending}
          onClick={() => void submit({ decline: true })}
          className="rounded px-3 py-1 text-label text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          Use your judgment
        </button>
        <span data-testid="question-card-default" className="text-caption text-muted-foreground">
          Default: {question.default}
        </span>
      </div>
    </div>
  );
}
